import { Box, Button, FlexLayout, H4, Icon, Modal } from '@deity-io/portal-ui';
import { TabItem, Tabs } from 'components';
import EntityMockData from 'components/EntityDetails/EntityMockData';
import { SaveEntityMockDataModal } from 'components/EntityDetails/SaveEntityMockDataModal';
import { IconButton } from 'components/IconButton/IconButton';
import { InputEditor } from 'components/InputEditor/InputEditor';
import { UIContext } from 'contexts/UIContext';
import { CACHE_FIRST } from 'contracts/constants';
import type { ICollectionType, ISchema } from 'contracts/interfaces';
import { useContext, useEffect, useState } from 'react';
import type React from 'react';
import {
  useGetMockDataLazyQuery,
  useGetModelQuery,
  useGetPackageVersionQuery,
  useRunMappingMutation
} from 'services/graphql/generated/hooks';
import { buildConfigurationStore } from 'utils/build-configuration-store';
import { MappingReport } from './MappingReport';

type StructureTestingProps = {
  mapping?: ICollectionType;
  mappingUuid?: string;
  locked?: boolean;
  packageVersionUuid: string;
};

export const MappingTesting: React.FC<StructureTestingProps> = ({
  mapping,
  mappingUuid,
  locked = false,
  packageVersionUuid
}) => {
  const ctxUI = useContext(UIContext);
  const [mappingState, setMappingState] = useState<{ mockData?: string; name?: string; mappingErrors?: any }>({});
  const [mockDataPickerState, setMockDataPickerState] = useState<{ open: boolean; selected?: string }>({ open: false });
  const [saveMockDataState, setSaveMockDataState] = useState<{ open: boolean }>({ open: false });
  const [activeTab, setActiveTab] = useState<string>('data');
  const sourceStructureUuid = mapping?._meta.source;
  const targetStructureUuid = mapping?._meta.target;

  const { data: sourceModelData } = useGetModelQuery({
    variables: {
      packageVersionUuid,
      uuid: sourceStructureUuid,
      expand: true
    },
    skip: !sourceStructureUuid,
    fetchPolicy: 'cache-first'
  });

  const { data: targetModelData } = useGetModelQuery({
    variables: {
      packageVersionUuid,
      uuid: targetStructureUuid,
      expand: true
    },
    skip: !targetStructureUuid,
    fetchPolicy: 'cache-first'
  });

  const { data: packageVersionData, loading: loadingPackageVersionData } = useGetPackageVersionQuery({
    variables: {
      uuid: packageVersionUuid
    },
    skip: !packageVersionUuid,
    ...CACHE_FIRST
  });

  const [getMockData, { data: loadedMockData }] = useGetMockDataLazyQuery(CACHE_FIRST);
  const [runMappingMutation, { data: runMappingResult, loading: runningMapping }] = useRunMappingMutation();

  useEffect(() => {
    setMappingState({
      ...mappingState,
      mockData: loadedMockData?.getMockData.data || '',
      name: loadedMockData?.getMockData.name || ''
    });
  }, [loadedMockData?.getMockData]);

  const mapData = () => {
    if (mappingState.mockData) {
      const availableVariableValues = buildConfigurationStore(ctxUI?.state.currentEnvironment, packageVersionData);
      runMappingMutation({
        variables: {
          data: mappingState.mockData,
          mapping: JSON.stringify(mapping),
          config_variables: availableVariableValues
        }
      }).catch(er => {
        setMappingState({ ...mappingState, mappingErrors: er.message }); // mappingErrors
        // eslint-disable-next-line no-console
        console.warn('Mutation Failed', er);
      });
    }
  };

  // const saveData = () => {
  //   if (mappingState.mockData) {
  //     if (loadedMockData) {
  //       saveDataMutation({
  //         variables: {
  //           description: '',
  //           name: mappingState.name || '',
  //           packageVersionUuid,
  //           modelUuid: sourceStructureUuid,
  //           mappingUuid,
  //           uuid: v4(),
  //           data: mappingState.mockData,
  //           source: {
  //             type: 'manual'
  //           }
  //         }
  //       });
  //     } else {
  //       updateDataMutation({
  //         variables: {
  //           description: '',
  //           name: mappingState.name || '',
  //           packageVersionUuid,
  //           modelUuid: sourceStructureUuid,
  //           mappingUuid,
  //           uuid: v4(),
  //           data: mappingState.mockData,
  //           source: {
  //             type: 'manual'
  //           }
  //         }
  //       });
  //     }
  //   }
  // };

  // let validateColor: IThemeColor = 'disabled';
  // if (!locked && mappingState.mockData) {
  //   validateColor = 'primary';
  //   if (schemaValidationResult) {
  //     if (schemaValidationResult.validateToSchema.valid) {
  //       validateColor = 'success';
  //       if (schemaValidationResult.validateToSchema.warnings?.length) {
  //         validateColor = 'warning';
  //       }
  //     } else {
  //       validateColor = 'error';
  //     }
  //   }
  // }
  return (
    <FlexLayout flexDirection="column" sx={{ padding: '10px' }}>
      <FlexLayout flexDirection="row" alignItems="center">
        <H4>{`Test data from "${mappingState.name}"` || 'Unsaved test data'}</H4>

        <Box key="toolButtons" sx={{ marginLeft: 'auto', marginRight: '0' }}>
          <IconButton
            key="select"
            tooltip="Load stored test data"
            icon="fileOpen"
            onClick={() => setMockDataPickerState({ ...mockDataPickerState, open: true })}
            disabled={locked}
          />
          <IconButton
            key="save"
            tooltip="Save test data"
            icon="save"
            onClick={() => {
              setSaveMockDataState({ ...saveMockDataState, open: true });
            }}
            disabled={locked || !mappingState.mockData}
          />

          <IconButton
            key="map"
            tooltip="Map tests data"
            icon="playArrow"
            onClick={() => {
              setActiveTab('output');
              mapData();
            }}
            disabled={locked || !mappingState.mockData}
          />
        </Box>
      </FlexLayout>
      <Tabs onChange={tab => {}}>
        <TabItem label="data" key="DataTab" isActive={activeTab === 'data'} keepAlive>
          <InputEditor
            value={mappingState.mockData}
            onChange={value => setMappingState({ ...mappingState, mockData: value })}
            disabled={locked}
            allowEmpty={false}
          />
        </TabItem>

        <TabItem
          label="output"
          key="OutputTab"
          keepAlive
          isActive={activeTab === 'output'}
          disabled={!mappingState.mockData}
        >
          {runMappingResult ? (
            <MappingReport
              report={runMappingResult.runMapping}
              targetModel={targetModelData?.getModel.schema as ISchema}
            />
          ) : (
            <FlexLayout
              width="full"
              justifyContent="center"
              alignItems="center"
              sx={{ minHeight: '100px', textAlign: 'center' }}
            >
              {runningMapping ? (
                'Running...'
              ) : (
                <Button endIcon="playArrow" onClick={mapData}>
                  Map
                </Button>
              )}
              {mappingState.mappingErrors && <Box>{mappingState.mappingErrors}</Box>}
            </FlexLayout>
          )}
        </TabItem>
      </Tabs>
      {mockDataPickerState.open && (
        <Modal
          key="EntityMockDataModel"
          isOpen={mockDataPickerState.open}
          onClose={() => setMockDataPickerState({ ...mockDataPickerState, open: false })}
          title="Select mock data to load."
          closeIcon={<Icon icon="close" />}
          confirmText="Load"
          onConfirm={props => {
            if (mockDataPickerState.selected) {
              getMockData({ variables: { packageVersionUuid, uuid: mockDataPickerState.selected } });
            }
            setMockDataPickerState({ ...mockDataPickerState, open: false, selected: undefined });
          }}
        >
          <EntityMockData
            packageVersionUuid={packageVersionUuid}
            modelUuid={sourceStructureUuid}
            onSelect={uuid => {
              setMockDataPickerState({ ...mockDataPickerState, selected: uuid });
            }}
          />
        </Modal>
      )}
      {saveMockDataState.open && mappingState.mockData && (
        <SaveEntityMockDataModal
          isOpen={saveMockDataState.open}
          setOpenState={isOpen => setSaveMockDataState({ ...saveMockDataState, open: isOpen })}
          packageVersionUuid={packageVersionUuid}
          mappingUuid={mappingUuid}
          modelUuid={sourceStructureUuid}
          onMockDataSaved={uuid => {
            getMockData({ variables: { packageVersionUuid, uuid } });
          }}
          editMockData={loadedMockData}
          mockData={mappingState.mockData}
        />
      )}
    </FlexLayout>
  );
};
